// ════════════════════════════════════════════════════════════════════════════
// CoachStore — the unified Coach/Toast FSM (plan: coach-tooltip-system.md §4.1).
//
// A GLOBAL-SINGLETON Zustand store (NOT origin-keyed — "you've seen the swipe tip"
// is a per-USER, app-wide fact) applying the workspace Frontend FSM pattern
// (server-config/docs/frontend-fsm-pattern.md):
//   1. Explicit FSM        — phase (idle | showing | dismissing) + exported TRANSITIONS.
//   2. Single active slot  — `active` + a priority `queue`; promotion only fills a
//                            FREE slot, so "never two at once" is structural.
//   3. Server-authoritative — reconcile(serverSeen) is the seam for a later L upgrade
//                            (persist `seen` to user_profile so a 2nd device isn't
//                            re-coached); v1 is degenerate (localStorage = store-of-record).
//   4. Atomic transitions  — one intent = one named action (offer/evaluate/dismiss/
//                            autoDismiss/markDiscovered/preempt/settle).
//   5. Setpoint evals      — setpoint-evals/coach/ drives THIS store (simulator + SEs).
//
// Two categories, one queue: `coach`/`pill` are teaching/sticky (peek, seen, caps);
// `toast` is transient feedback (fire-every-time, no seen-gate). Errors PREEMPT an
// active coach by REQUEUING it (forced) — never dropping it. The <CoachLayer/>
// (shared-ui) is the only renderer; the coachWatcher (triggers) lives in the shells'
// useChatShell. Built + validated Phase-0-first against setpoint-evals/coach/.
//
// Promotion has TWO paths (Phase 3b): `evaluate(snapshot)` is the domain-aware scan
// (coach rows only — needs reachableWhen), fed by the swipe effect's chat-view-entry
// tick. `promoteImmediate()` is the snapshot-free path for toast/pill/forced-coach
// (none of which consult reachableWhen) — called from show()/preempt() so enqueueing
// a toast shows it without waiting for the next domain tick, and from CoachLayer
// right after settle() so a queued toast/forced-coach gets the freed slot.
// ════════════════════════════════════════════════════════════════════════════

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

// ─── Types (the frozen public contract the SEs pin) ─────────────────────────────

export type CoachKind = 'coach' | 'toast' | 'pill';
export type CoachPhase = 'idle' | 'showing' | 'dismissing';
export type Anchor = 'viewport' | { selector: string };

export interface CoachInstance {
  id: string;
  kind: CoachKind;
  anchor: Anchor;
  body: string;
  persona?: boolean;
  priority: number;
  ttlMs?: number;
  dismissible: boolean;
  role: 'status' | 'alert';
  /** Set when a coach was preempted (requeued) — exempt from the seen + one-per-launch
   *  gates on re-promotion, because it already WAS the one coach this launch. */
  forced?: boolean;
  /** Optional secondary CTA (e.g. "Install") rendered before the x dismiss — the
   *  pwa-install pill needs an action beyond dismiss. Kind-agnostic; not persisted
   *  (active/queue are runtime-only, so a closure here is safe). */
  action?: { label: string; onClick: () => void };
  /** Optional side effect the x dismiss ALSO invokes, on top of the store's own
   *  dismiss(id) — e.g. the pwa-install pill's 30-day-cooldown localStorage write.
   *  Additive, never a replacement for dismiss(id). */
  onDismiss?: () => void;
}

export interface ShowRequest {
  id: string;
  kind: CoachKind;
  anchor?: Anchor;
  body: string;
  persona?: boolean;
  priority?: number;
  ttlMs?: number;
  dismissible?: boolean;
  role?: 'status' | 'alert';
  action?: { label: string; onClick: () => void };
  onDismiss?: () => void;
}

/** The per-tick authoritative read the CoachLayer hands to evaluate(). reachableWhen
 *  predicates + the anchor-mounted gate consume this. */
export interface CoachSnapshot {
  chatViewEntries: number;
  swipeableAgents: number;
  fabVisible: boolean;
  newSessionsCreated: number;
  assistantMsgCount: number;
  composerFocusedEmptyMs: number;
  drawerOpenedCount: number;
  sessionCount: number;
  /** data-coach-id selectors currently mounted in the DOM. An element-anchored row
   *  whose selector is absent is structurally un-promotable (the SE-09 can't-happen
   *  guarantee — anchor-presence is a STORE contract via the snapshot, not a DOM
   *  callback). */
  mountedAnchors: string[];
}

export interface CoachCatalogRow {
  id: string;
  kind: CoachKind;
  anchor: Anchor;
  reachableWhen: (s: CoachSnapshot) => boolean;
  discoverSignal?: string;
  body: string;
  persona?: boolean;
  cap: 1 | 2;
  priority: number;
  ttlMs?: number;
  persistKey?: string;
}

export interface SeenEntry {
  count: number;
  lastAt: number;
}

export interface CoachState {
  phase: CoachPhase;
  active: CoachInstance | null;
  queue: CoachInstance[];
  seen: Record<string, SeenEntry>;
  armed: boolean;
  shownThisLaunch: boolean;
  coachingEnabled: boolean;
}

// ─── Anchor selector (single source — drift-proof) ──────────────────────────────
// The element-anchored catalog rows AND the live snapshot's `mountedAnchors` MUST
// produce byte-identical selector strings, or the row is silently never-eligible
// (isEligible's anchor gate is exact string equality — and the catalog uses the
// UNQUOTED attribute form `[data-coach-id=fab]`). Route BOTH through this one helper
// so they can't drift; SE-16 asserts the catalog rows === coachAnchorSelector(id).
export function coachAnchorSelector(id: string): string {
  return `[data-coach-id=${id}]`;
}

// ─── Declarative catalog (the spine) ────────────────────────────────────────────

export const COACH_CATALOG: CoachCatalogRow[] = [
  {
    id: 'swipe',
    kind: 'coach',
    anchor: 'viewport',
    reachableWhen: (s) => s.chatViewEntries >= 3 && s.swipeableAgents >= 2,
    discoverSignal: 'swipe',
    body: 'Swipe left or right to jump between your agents.',
    cap: 1,
    priority: 10,
    ttlMs: 8000,
    persistKey: 'voice-assistant-swipe-hinted',
  },
  {
    id: 'model-picker',
    kind: 'coach',
    anchor: { selector: coachAnchorSelector('fab') },
    reachableWhen: (s) => s.fabVisible && s.newSessionsCreated >= 1,
    body: 'Hold + to pick which model I think with.',
    cap: 1,
    priority: 20,
    persistKey: 'voice-assistant-fixed-model-hinted',
  },
  {
    id: 'double-tap-quote',
    kind: 'coach',
    anchor: 'viewport',
    reachableWhen: (s) => s.assistantMsgCount >= 1,
    discoverSignal: 'double-tap',
    body: 'Double-tap any paragraph to quote it back to me.',
    cap: 1,
    priority: 15,
    persistKey: 'va-doubletap-hint',
  },
  {
    id: 'reader-mode',
    kind: 'coach',
    anchor: { selector: coachAnchorSelector('reader') },
    reachableWhen: (s) => s.assistantMsgCount >= 1,
    body: 'Tap reader mode for a calmer view of long replies.',
    cap: 1,
    priority: 12,
  },
  {
    id: 'agents-nav',
    kind: 'coach',
    anchor: { selector: coachAnchorSelector('agents-nav') },
    reachableWhen: (s) => s.sessionCount >= 2 && s.drawerOpenedCount === 0,
    body: 'All our threads live under Agents.',
    cap: 1,
    priority: 6,
  },
  {
    id: 'ptt',
    kind: 'coach',
    anchor: { selector: coachAnchorSelector('mic') },
    reachableWhen: (s) => s.composerFocusedEmptyMs > 12000,
    body: 'Hold the mic to talk instead of typing.',
    cap: 1,
    priority: 8,
  },
];

// ─── Transition table (R-A1) ────────────────────────────────────────────────────
// SE-10-transitions-coverage validates that EVERY distinct `trigger` here is covered
// by at least one scenario's `// SE-COVERS:` marker. `showing → showing` is
// forbidden: a preempt is dismiss-then-promote (two transitions). The slot-occupied
// guard in evaluate() (active !== null → no-op) is NOT a transition — it fires no
// trigger.

export const TRANSITIONS: Array<{ from: CoachPhase; to: CoachPhase; trigger: string }> = [
  { from: 'idle', to: 'showing', trigger: 'promote' },
  { from: 'idle', to: 'idle', trigger: 'evaluate:no-match' },
  { from: 'showing', to: 'dismissing', trigger: 'dismiss' },
  { from: 'showing', to: 'dismissing', trigger: 'autoDismiss' },
  { from: 'showing', to: 'dismissing', trigger: 'markDiscovered' },
  { from: 'showing', to: 'dismissing', trigger: 'preempt' },
  { from: 'dismissing', to: 'idle', trigger: 'settled' },
];

// ─── Helpers ────────────────────────────────────────────────────────────────────

function anchorSelector(a: Anchor): string | null {
  return a === 'viewport' ? null : a.selector;
}

function capFor(id: string): number {
  return COACH_CATALOG.find((r) => r.id === id)?.cap ?? 1;
}

function rowFor(id: string): CoachCatalogRow | undefined {
  return COACH_CATALOG.find((r) => r.id === id);
}

function instanceFromRow(row: CoachCatalogRow): CoachInstance {
  return {
    id: row.id,
    kind: row.kind,
    anchor: row.anchor,
    body: row.body,
    persona: row.persona,
    priority: row.priority,
    ttlMs: row.ttlMs,
    dismissible: true,
    role: 'status',
  };
}

function instanceFromRequest(req: ShowRequest): CoachInstance {
  return {
    id: req.id,
    kind: req.kind,
    anchor: req.anchor ?? 'viewport',
    body: req.body,
    persona: req.persona,
    priority: req.priority ?? (req.kind === 'toast' ? 100 : 10),
    ttlMs: req.ttlMs,
    dismissible: req.dismissible ?? true,
    role: req.role ?? 'status',
    action: req.action,
    onDismiss: req.onDismiss,
  };
}

// ─── Store creation ─────────────────────────────────────────────────────────────

export const useCoachStore = create<CoachState>()(
  persist(
    (): CoachState => ({
      phase: 'idle',
      active: null,
      queue: [],
      seen: {},
      armed: false,
      shownThisLaunch: false,
      coachingEnabled: true,
    }),
    {
      name: 'va-coach-v1',
      storage: createJSONStorage(() => localStorage),
      // Persist ONLY the per-user facts: which tips were seen + the kill-switch.
      // active/queue/phase/armed/shownThisLaunch are runtime-only → never trust a
      // persisted live surface (pattern rule 3).
      partialize: (state) => ({
        seen: state.seen,
        coachingEnabled: state.coachingEnabled,
      }),
      // Seen-persistence + legacy migration (§4.1): back-fill `seen` from the
      // confirmed legacy keys so power users are NOT re-coached on deploy day.
      // Idempotent + one-way; leaves legacy keys in place.
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const seen: Record<string, SeenEntry> = { ...state.seen };
        const seedLocal = (legacyKey: string, id: string) => {
          try {
            if (localStorage.getItem(legacyKey) != null && !(id in seen)) {
              seen[id] = { count: capFor(id), lastAt: 0 };
            }
          } catch {
            /* storage unavailable — skip */
          }
        };
        const seedSession = (legacyKey: string, id: string) => {
          try {
            if (sessionStorage.getItem(legacyKey) != null && !(id in seen)) {
              seen[id] = { count: capFor(id), lastAt: 0 };
            }
          } catch {
            /* storage unavailable — skip */
          }
        };
        seedLocal('voice-assistant-swipe-hinted', 'swipe');
        seedLocal('voice-assistant-fixed-model-hinted', 'model-picker');
        seedLocal('va-doubletap-hint-count', 'double-tap-quote');
        seedSession('va-doubletap-hint-shown', 'double-tap-quote');
        useCoachStore.setState({ seen });
      },
    },
  ),
);

// ─── Internal: enqueue with id-dedupe ────────────────────────────────────────────

function enqueue(inst: CoachInstance): void {
  useCoachStore.setState((s) => {
    if (s.active?.id === inst.id) return s;
    if (s.queue.some((q) => q.id === inst.id)) return s;
    return { queue: [...s.queue, inst] };
  });
}

// ─── Eligibility (pure) ──────────────────────────────────────────────────────────

function isEligible(s: CoachState, inst: CoachInstance, snap: CoachSnapshot): boolean {
  // Anchor must be mounted (element-anchored rows only). The can't-happen guarantee.
  const sel = anchorSelector(inst.anchor);
  if (sel !== null && !snap.mountedAnchors.includes(sel)) return false;

  // A preempted (forced) coach resumes regardless of seen / launch gates.
  if (inst.forced) return true;

  if (inst.kind === 'coach') {
    if (s.shownThisLaunch) return false; // one gentle coach per launch
    if ((s.seen[inst.id]?.count ?? 0) >= capFor(inst.id)) return false; // seen-gated
    const row = rowFor(inst.id);
    if (row && !row.reachableWhen(snap)) return false;
  }
  // toast / pill: not seen-gated, recur freely (anchor permitting).
  return true;
}

// ─── Snapshot-free promotion (toast/pill/forced-coach) ──────────────────────────
// `evaluate(snapshot)` is the ONLY promotion path, and it is fed by exactly one call
// site today (the swipe-hint effect in useChatShell — a chat-view-entry tick). A
// `kind:'toast'`/`'pill'` row — or a `forced` (preempted, requeued) coach — never
// consults `reachableWhen(snapshot)` (isEligible only checks it inside the
// `kind==='coach' && !forced` branch), so it doesn't need the real domain snapshot
// to know it's eligible; it only needs (a) the slot free and (b) its anchor mounted
// (all current toast/pill rows are 'viewport', so that check is a no-op). Without
// this, a toast enqueued outside the swipe effect's tick — e.g. billing/mic-error/
// resume — would sit in queue until the next unrelated chat-view-entry, or forever.
// Called from show()/preempt() (the enqueue sites) so promotion-on-enqueue is
// automatic; CoachLayer also calls it right after settle() so a toast queued behind
// an active occupant gets its turn once the slot frees — see CoachLayer.tsx.

// A placeholder snapshot for promoteImmediate()'s anchor-mounted check. Safe because
// every candidate it filters for (kind!=='coach', or forced) never consults
// reachableWhen — only the anchor gate applies, and it's a no-op for 'viewport'.
const VIEWPORT_SNAPSHOT: CoachSnapshot = {
  chatViewEntries: 0,
  swipeableAgents: 0,
  fabVisible: false,
  newSessionsCreated: 0,
  assistantMsgCount: 0,
  composerFocusedEmptyMs: 0,
  drawerOpenedCount: 0,
  sessionCount: 0,
  mountedAnchors: [],
};

export function promoteImmediate(): void {
  useCoachStore.setState((s) => {
    if (s.active !== null) return s;
    const eligible = s.queue.filter(
      (inst) => (inst.forced || inst.kind !== 'coach') && isEligible(s, inst, VIEWPORT_SNAPSHOT),
    );
    if (eligible.length === 0) return s;
    const best = eligible.reduce((a, b) => (b.priority > a.priority ? b : a));
    const queue = s.queue.filter((q) => q.id !== best.id);
    return { phase: 'showing', active: { ...best, forced: undefined }, queue };
  });
}

// ─── Named actions (one per intent / transition — R-A2/R-A4) ────────────────────

/** Registry-driven enqueue of a catalog coach row by id. */
export function offer(id: string): void {
  const row = rowFor(id);
  if (!row) return;
  enqueue(instanceFromRow(row));
}

/** Explicit enqueue of a toast/pill (or ad-hoc coach) instance. Self-promotes if the
 *  slot is free (promoteImmediate) — a toast doesn't wait for the next evaluate(). */
export function show(req: ShowRequest): void {
  enqueue(instanceFromRequest(req));
  promoteImmediate();
}

/**
 * The per-tick pure scan the CoachLayer runs. Promotes the single highest-priority
 * eligible instance into `active` IFF the slot is free + armed + coaching enabled
 * (idle → showing, `promote`). Otherwise leaves the FSM idle (`evaluate:no-match`).
 * The `active !== null` guard is what makes "never two at once" structural: two
 * stores updating in the same tick still yield exactly one promotion.
 */
export function evaluate(snapshot: CoachSnapshot): void {
  useCoachStore.setState((s) => {
    if (s.active !== null) return s; // slot occupied — guard (no trigger)
    if (!s.armed || !s.coachingEnabled) return s; // armed-gate / kill-switch → no-match
    const eligible = s.queue.filter((inst) => isEligible(s, inst, snapshot));
    if (eligible.length === 0) return s; // evaluate:no-match
    const best = eligible.reduce((a, b) => (b.priority > a.priority ? b : a));
    const queue = s.queue.filter((q) => q.id !== best.id);

    const promoted: CoachInstance = { ...best, forced: undefined };
    const seen = { ...s.seen };
    let shownThisLaunch = s.shownThisLaunch;
    if (best.kind === 'coach' && !best.forced) {
      seen[best.id] = { count: (seen[best.id]?.count ?? 0) + 1, lastAt: Date.now() };
      shownThisLaunch = true;
    }
    return { phase: 'showing', active: promoted, queue, seen, shownThisLaunch };
  });
}

/** User ×/esc/tap-outside → showing → dismissing. */
export function dismiss(id: string): void {
  useCoachStore.setState((s) =>
    s.phase === 'showing' && s.active?.id === id ? { phase: 'dismissing' } : s,
  );
}

/** ttl elapsed → showing → dismissing. */
export function autoDismiss(id: string): void {
  useCoachStore.setState((s) =>
    s.phase === 'showing' && s.active?.id === id ? { phase: 'dismissing' } : s,
  );
}

/**
 * Real gesture performed → mark seen (so it never re-shows) AND cancel mid-display
 * if it is the active tip (showing → dismissing). Usage-implies-discovery.
 */
export function markDiscovered(id: string): void {
  useCoachStore.setState((s) => {
    const prev = s.seen[id]?.count ?? 0;
    const seen = { ...s.seen, [id]: { count: Math.max(prev, capFor(id)), lastAt: Date.now() } };
    const shownThisLaunch =
      rowFor(id)?.kind === 'coach' || s.active?.id === id ? true : s.shownThisLaunch;
    if (s.phase === 'showing' && s.active?.id === id) {
      return { seen, phase: 'dismissing', shownThisLaunch };
    }
    return { seen, shownThisLaunch };
  });
}

/**
 * A high-priority toast/error preempts an active coach. The coach is REQUEUED (never
 * dropped) with a `forced` flag so it resumes after the toast clears; the slot is
 * vacated (showing → dismissing). If nothing is currently showing, the preempting
 * toast self-promotes immediately (promoteImmediate) — an error doesn't wait for the
 * next evaluate() tick either. Once settle() frees the slot, CoachLayer's own
 * promoteImmediate() call picks up the toast, then later the requeued forced coach.
 */
export function preempt(req: ShowRequest): void {
  const inst = instanceFromRequest(req);
  useCoachStore.setState((s) => {
    let queue = s.queue.some((q) => q.id === inst.id) ? s.queue : [...s.queue, inst];
    if (s.phase === 'showing' && s.active && s.active.kind !== 'toast') {
      const requeued: CoachInstance = { ...s.active, forced: true };
      if (!queue.some((q) => q.id === requeued.id)) queue = [requeued, ...queue];
      return { queue, phase: 'dismissing' };
    }
    return { queue };
  });
  promoteImmediate();
}

/** Exit animation finished (instant under reduced-motion) → dismissing → idle, slot
 *  freed. CoachLayer calls promoteImmediate() right after, so a queued toast/pill/
 *  forced-coach gets the freed slot without waiting for the next evaluate() tick. */
export function settle(): void {
  useCoachStore.setState((s) =>
    s.phase === 'dismissing' ? { phase: 'idle', active: null } : s,
  );
}

/**
 * Server-authoritative reconcile seam (degenerate v1 — localStorage is store-of-record).
 * The signature is pre-built so a later L upgrade (persist `seen` to user_profile so a
 * 2nd device isn't re-coached) is additive, not a refactor.
 */
export function reconcile(serverSeen: Record<string, SeenEntry>): void {
  useCoachStore.setState((s) => ({ seen: { ...serverSeen, ...s.seen } }));
}

/**
 * Onboarding settled (the 3 first-run modals closed + a quiet window) → coaching is
 * allowed. `armed` is runtime-only (not persisted), so this is re-asserted each launch.
 * evaluate() is a no-op while `armed === false` — no coach can stack on a modal wall.
 */
export function arm(): void {
  useCoachStore.setState({ armed: true });
}

/** Settings kill-switch ("Show coaching tips"). Persisted via partialize. */
export function setCoachingEnabled(enabled: boolean): void {
  useCoachStore.setState({ coachingEnabled: enabled });
}

// ─── Persona greeting gate (§5.1) ────────────────────────────────────────────────
// The persona greeting is in-thread content (a chat message), not a CoachLayer
// popover — so it does NOT go through offer/evaluate/the active slot. It reuses the
// store's `seen` map purely as its "already shown" ledger (via markDiscovered, which
// useSocket.ts calls once it has posted the message) so resetFirstRunCoaching's
// reset is the single place that un-gates BOTH mechanisms at once.

export const PERSONA_GREETING_ID = 'persona-greeting';

/**
 * Pure decision function — no store reads inside useSocket.ts's newSession() closure,
 * so this is independently testable (SE-20). Gated on:
 *   (a) this is genuinely the user's first-ever session (sessionCountBeforeCreate===0)
 *       — NOT re-shown to an existing user creating their 2nd+/Nth session ever;
 *   (b) not already seen (idempotent — a fast double-tap of "+" must not double-post);
 *   (c) the coaching kill-switch is respected (§5.3: "persona greeting suppressed").
 * Deliberately NOT gated on `armed` — the greeting is a plain chat message, not a
 * CoachLayer popover, so it can't visually stack on the onboarding modals; gating it
 * on the same 4s settle timer as popovers would risk losing the greeting entirely if
 * the user creates their first session inside that window.
 */
export function shouldGreetPersona(opts: {
  sessionCountBeforeCreate: number;
  seen: Record<string, SeenEntry>;
  coachingEnabled: boolean;
}): boolean {
  return (
    opts.sessionCountBeforeCreate === 0 &&
    opts.coachingEnabled &&
    !(PERSONA_GREETING_ID in opts.seen)
  );
}

/**
 * Replay "?" (GearMenu/More, §5.4): clears `seen` for every catalog `coach` row PLUS
 * the persona-greeting id, and resets `shownThisLaunch` — so a returning user who
 * asks to see the intro again gets a genuine re-arm, not a no-op against stale seen
 * entries. Does NOT touch toast/pill seen-state (those never gate on seen anyway).
 * The persona-greeting message itself is re-posted by the caller (useSocket.ts owns
 * message injection, not this store); this only clears the gating state.
 */
export function resetFirstRunCoaching(): void {
  useCoachStore.setState((s) => {
    const seen = { ...s.seen };
    for (const row of COACH_CATALOG) delete seen[row.id];
    delete seen[PERSONA_GREETING_ID];
    return { seen, shownThisLaunch: false };
  });
}

// ─── Hook selectors ──────────────────────────────────────────────────────────────

export function useActiveCoach(): CoachInstance | null {
  return useCoachStore((s) => s.active);
}

export function useCoachPhase(): CoachPhase {
  return useCoachStore((s) => s.phase);
}

export function useCoachView() {
  return useCoachStore(useShallow((s) => ({ phase: s.phase, active: s.active })));
}
